import {
  EllipsisHorizontalIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  CalendarIcon,
  CaretSortIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
} from "@radix-ui/react-icons";
import {
  ColumnDef,
  Row,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { BarChart, Legend } from "@tremor/react";
import clsx from "clsx";
import dayjs from "dayjs";
import parse from "parse-duration";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useGetUserSettings } from "src/api/General/settings";
import {
  ProjectData,
  useGetProjects,
} from "src/api/Services/Projects/projects";
import { TaskData, useGetPhaseTasks } from "src/api/Services/Tasks/tasks";
import {
  CalendarEventData,
  TimeEntryData,
  TimesheetSummaryData,
  useCreateTimeEntries,
  useDeleteTimesheetEntry,
  useGetCalendarEvents,
  useGetTimesheet,
  useGetTimesheetEntriesForUser,
  useGetTimesheetEntriesForWeek,
  useUpdateTimeEntry,
  useUpdateTimesheet,
} from "src/api/timesheets/timesheets";
import { useSnackBar } from "src/components/Reusable/CustomSnackbarProvider";
import { Button } from "src/components/ui/button";
import { Checkbox } from "src/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import InputHeading from "src/components/ui/Headings/InputHeading";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";
import { Loading } from "src/components/ui/loading";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import { SecondaryCheckbox } from "src/components/ui/secondary-checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import { TextInput } from "src/components/ui/text-input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { useRealmApp } from "src/store/RealmApp";
import { convertMsToNatural, getDateOfMonday } from "../timesheets/timesheets";
import NewEntryDialog from "./new-entry";

type Props = {};

export default function TimesheetsTab({}: Props) {
  const { tab } = useParams();
  const navigate = useNavigate();
  if (!tab) {
    navigate(`/time-tracking/timesheets/my-timesheet`, {
      replace: true,
    });
  } else {
    if (tab !== "my-timesheet" && tab !== "all-timesheets") {
      navigate(`/time-tracking/timesheets/my-timesheet`, {
        replace: true,
      });
    }
  }
  // useEffect(() => {
  //   if (!tab) {
  //     navigate(`/timesheets/my-timesheet`);
  //   } else {
  //     if (tab !== "my-timesheet" && tab !== "all-timesheets") {
  //       navigate(`/timesheets/my-timesheet`, {
  //         replace: true,
  //       });
  //     }
  //   }
  // }, [tab]);

  return (
    <div className="flex h-full flex-col overflow-auto">
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70 px-8 text-sm text-gray-800">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/time-tracking`}>
            <div className="text-gray-800">{"Time Tracking"}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Timesheets"}</div>
        </div>
      </div>

      <Tabs
        defaultValue="my-timesheet"
        value={tab}
        className="flex flex-1 flex-col overflow-auto pt-3"
        onValueChange={(value) => {
          navigate(`/time-tracking/timesheets/${value}`);
        }}
      >
        <TabsList className="shrink-0 border-gray-200/70 px-8">
          <TabsTrigger
            className="font-normal text-gray-500"
            value="my-timesheet"
          >
            My Timesheet
          </TabsTrigger>
          <TabsTrigger
            className="font-normal text-gray-500"
            value="all-timesheets"
          >
            All Timesheets
          </TabsTrigger>
          {/* <TabsTrigger
              className="font-normal text-gray-500"
              value="all-timesheets"
            >
              All Timesheets
            </TabsTrigger> */}
        </TabsList>
        <div className="flex-1 overflow-auto">
          <TabsContent
            value="my-timesheet"
            className="flex flex-col overflow-auto"
          >
            <MyTimesheet />
          </TabsContent>
          <TabsContent
            value="all-timesheets"
            className="flex flex-col overflow-auto"
          >
            <AllTimesheets />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

function convertMsToHours(ms: number) {
  return Number((ms / 1000 / 60 / 60).toFixed(1));
}

const getDaysOfWeek = (currentWeek: Date): string[] => {
  const days: string[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(currentWeek);
    // Adjust to start from Monday (1) instead of Sunday (0)
    day.setDate(currentWeek.getDate() - currentWeek.getDay() + i + 1);
    days.push(dayjs(day).format("YYYY-MM-DD"));
  }
  return days;
};

function MyTimesheet() {
  const [currentWeek, setCurrentWeek] = useState(getDateOfMonday(new Date()));
  const { data: timesheetEntries } = useGetTimesheetEntriesForUser(
    dayjs(currentWeek).format("YYYY-MM-DD")
  );

  const goToPreviousWeek = () => {
    setCurrentWeek((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };

  const goToNextWeek = () => {
    setCurrentWeek((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };

  const time: {
    day: string;
    "Billable Hours": number;
    "Non-Billable Hours": number;
  }[] = [
    {
      day: "Mon",
      "Billable Hours": convertMsToHours(
        timesheetEntries
          ?.filter((entry) => entry.isBillable && dayjs(entry.date).day() === 1)
          .reduce((acc, entry) => acc + entry.time, 0) ?? 0
      ),
      "Non-Billable Hours": convertMsToHours(
        timesheetEntries
          ?.filter(
            (entry) => !entry.isBillable && dayjs(entry.date).day() === 1
          )
          .reduce((acc, entry) => acc + entry.time, 0) ?? 0
      ),
    },
    {
      day: "Tue",
      "Billable Hours": convertMsToHours(
        timesheetEntries
          ?.filter((entry) => entry.isBillable && dayjs(entry.date).day() === 2)
          .reduce((acc, entry) => acc + entry.time, 0) ?? 0
      ),
      "Non-Billable Hours": convertMsToHours(
        timesheetEntries
          ?.filter(
            (entry) => !entry.isBillable && dayjs(entry.date).day() === 2
          )
          .reduce((acc, entry) => acc + entry.time, 0) ?? 0
      ),
    },
    {
      day: "Wed",
      "Billable Hours": convertMsToHours(
        timesheetEntries
          ?.filter((entry) => entry.isBillable && dayjs(entry.date).day() === 3)
          .reduce((acc, entry) => acc + entry.time, 0) ?? 0
      ),
      "Non-Billable Hours": convertMsToHours(
        timesheetEntries
          ?.filter(
            (entry) => !entry.isBillable && dayjs(entry.date).day() === 3
          )
          .reduce((acc, entry) => acc + entry.time, 0) ?? 0
      ),
    },
    {
      day: "Thu",
      "Billable Hours": convertMsToHours(
        timesheetEntries
          ?.filter((entry) => entry.isBillable && dayjs(entry.date).day() === 4)
          .reduce((acc, entry) => acc + entry.time, 0) ?? 0
      ),
      "Non-Billable Hours": convertMsToHours(
        timesheetEntries
          ?.filter(
            (entry) => !entry.isBillable && dayjs(entry.date).day() === 4
          )
          .reduce((acc, entry) => acc + entry.time, 0) ?? 0
      ),
    },
    {
      day: "Fri",
      "Billable Hours": convertMsToHours(
        timesheetEntries
          ?.filter((entry) => entry.isBillable && dayjs(entry.date).day() === 5)
          .reduce((acc, entry) => acc + entry.time, 0) ?? 0
      ),
      "Non-Billable Hours": convertMsToHours(
        timesheetEntries
          ?.filter(
            (entry) => !entry.isBillable && dayjs(entry.date).day() === 5
          )
          .reduce((acc, entry) => acc + entry.time, 0) ?? 0
      ),
    },
  ];
  const [isStacked, setIsStacked] = useState(false);
  const [groupedBy, setGroupedBy] = useState<"day" | "week">("day");
  const [entriesGroupedBy, setEntriesGroupedBy] = useState<"none" | "project">(
    "none"
  );

  // const entries: {
  //   project: string;
  //   stage: string;
  //   task: string;
  //   time: number;
  //   description: string;
  // }[] = [
  //   // {
  //   //   project: "Project 1",
  //   //   stage: "Stage 1",
  //   //   task: "Task 1",
  //   //   time: 1000 * 60 * 60 * 5,
  //   //   description: "This is the description for this task",
  //   // },
  // ];

  const totalTime = useMemo(() => {
    return timesheetEntries?.reduce((acc, entry) => acc + entry.time, 0) ?? 0;
  }, [timesheetEntries]);
  const billableTime = useMemo(() => {
    return (
      timesheetEntries
        ?.filter((entry) => entry.isBillable)
        .reduce((acc, entry) => acc + entry.time, 0) ?? 0
    );
  }, [timesheetEntries]);
  const nonBillableTime = useMemo(() => {
    return (
      timesheetEntries
        ?.filter((entry) => !entry.isBillable)
        .reduce((acc, entry) => acc + entry.time, 0) ?? 0
    );
  }, [timesheetEntries]);
  const daysOfWeek = useMemo(() => getDaysOfWeek(currentWeek), [currentWeek]);

  const [newEntryOptionsOpen, setNewEntryOptionsOpen] = useState(false);
  const [newEntryDialogOpen, setNewEntryDialogOpen] = useState(false);
  const [addEntriesFromCalendarOpen, setAddEntriesFromCalendarOpen] =
    useState(false);
  const submitTimesheet = useUpdateTimesheet();
  const { data: timesheet } = useGetTimesheet(
    dayjs(currentWeek).format("YYYY-MM-DD")
  );

  return (
    <>
      <AddEntriesFromCalendar
        currentWeek={currentWeek}
        open={addEntriesFromCalendarOpen}
        setOpen={setAddEntriesFromCalendarOpen}
      />
      <NewEntryDialog
        open={newEntryDialogOpen}
        onOpenChange={(isOpen) => {
          setNewEntryDialogOpen(isOpen);
        }}
      />
      <div className="flex h-14 items-center justify-between border-b border-gray-200/70 px-8">
        <div className={clsx("flex rounded-md shadow-sm")}>
          <button
            className={clsx(
              "flex h-8 w-7 items-center justify-center rounded-l-md border border-input text-gray-500 transition-all hover:bg-gray-50 hover:text-gray-700"
            )}
            onClick={goToPreviousWeek}
          >
            <ChevronLeftIcon className="h-3 w-3" />
          </button>
          <div className="flex w-[136px] items-center justify-center border-y border-input px-1 text-sm font-medium text-gray-700 hover:bg-gray-50">
            {`${dayjs(daysOfWeek[0]).format("MMM DD")} - ${dayjs(
              daysOfWeek[6]
            ).format("MMM DD")}`}
          </div>
          <button
            className={clsx(
              "flex h-8 w-7 items-center justify-center rounded-r-md border border-input text-gray-500 transition-all hover:bg-gray-50 hover:text-gray-700"
            )}
            onClick={goToNextWeek}
          >
            <ChevronRightIcon className="h-3 w-3" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          {/* {timesheet?.exists && (
            <div className="flex items-center gap-2">
              {timesheet.timesheet.isSubmitted &&
                timesheet.timesheet.submittedAt && (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <CheckBadgeIcon className="h-5 w-5 text-gray-600" />
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <div>
                          Timesheet last submitted at{" "}
                          {dayjs(timesheet.timesheet.submittedAt).format(
                            "hh:mm A, ddd MMM DD, YYYY"
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              <Button
                disabled={submitTimesheet.isLoading}
                variant={"secondary"}
                onClick={() => {
                  if (timesheet?.exists) {
                    submitTimesheet.mutate({
                      _id: timesheet.timesheet._id,
                      isSubmitted: true,
                    });
                  }
                }}
              >
                {submitTimesheet.isLoading
                  ? timesheet.timesheet.isSubmitted
                    ? "Resubmitting.."
                    : "Submitting.."
                  : timesheet.timesheet.isSubmitted
                  ? "Resubmit"
                  : "Submit"}
              </Button>
            </div>
          )} */}
          <div className="flex h-8 items-center">
            <button
              className="flex h-full items-center rounded-l-md border border-r border-input px-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => {
                setNewEntryDialogOpen(true);
              }}
            >
              New Entry
            </button>
            <DropdownMenu
              open={newEntryOptionsOpen}
              onOpenChange={(isOpen) => {
                setNewEntryOptionsOpen(isOpen);
              }}
            >
              <DropdownMenuTrigger
                asChild
                className="flex h-8 w-7 items-center justify-center"
              >
                <button className="rounded-r-md border border-l-0 border-input hover:bg-gray-50">
                  <PlusIcon className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-44" align="end" sideOffset={4}>
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    // className="py-1 focus:bg-gray-50 focus:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddEntriesFromCalendarOpen(true);
                    }}
                    // className="py-1"
                  >
                    <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="ml-2 text-sm">Calendar Import</span>
                  </DropdownMenuItem>
                  {/* <DropdownMenuItem
                    // className="py-1 focus:bg-gray-50 focus:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      // setAddEntriesFromCalendarOpen(true);
                    }}
                    // className="py-1"
                  >
                    <BoltIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="ml-2 text-sm">Bulk Task Import</span>
                  </DropdownMenuItem> */}
                  {/* <DropdownMenuItem
                    // className="py-1 focus:bg-gray-50 focus:text-red-600"
                    onClick={(e) => {
                      setIsReordering(true);
                    }}
                  >
                    <ArrowsUpDownIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="ml-2 text-sm">Reorder</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    // className="py-1 focus:bg-gray-50 focus:text-red-600"
                    className="focus:bg-red-50 focus:text-red-600"
                    onClick={(e) => {
                      setIsDeleting(true);
                      e.stopPropagation();
                    }}
                  >
                    <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="ml-2 text-sm">Delete</span>
                  </DropdownMenuItem> */}
                  {/* <DropdownMenuItem
                      // className="py-1 focus:bg-gray-50 focus:text-red-600"
                      className="focus:bg-red-50 focus:text-red-600"
                      onClick={(e) => {
                        setIsDeleting(true);
                        e.stopPropagation();
                      }}
                    >
                      <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                      <span className="ml-2 text-sm">Delete</span>
                    </DropdownMenuItem> */}
                  {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* <Button variant={"secondary"}>
            <div className="flex items-center gap-2">
              New Entry
              <ChevronDownIcon className="h-4 w-4" />
            </div>
          </Button> */}
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <div className="w-full border-b border-gray-200/70">
          <div className="flex items-center justify-center gap-3 bg-gray-50 p-6">
            <div className="h-72 w-64 space-y-7 rounded-md border bg-white p-8 shadow-sm">
              <div className="space-y-0.5">
                <div className="text-xs font-light text-gray-600">
                  Total Time
                </div>
                <div>
                  <div className="text-lg font-medium">
                    {convertMsToNatural(totalTime)}
                  </div>
                </div>
              </div>
              <div className="space-y-0.5">
                <div className="text-xs font-light text-gray-600">
                  Billable Time
                </div>
                <div>
                  <div className="text-lg font-medium">
                    {convertMsToNatural(billableTime)}
                  </div>
                </div>
              </div>
              <div className="space-y-0.5">
                <div className="text-xs font-light text-gray-600">
                  Non-Billable Time
                </div>
                <div>
                  <div className="text-lg font-medium">
                    {convertMsToNatural(nonBillableTime)}
                  </div>
                </div>
              </div>
            </div>

            <div
              className={clsx(
                "h-72 w-full min-w-0 max-w-xl rounded-md border bg-white p-8 pl-0 shadow-sm"
              )}
            >
              <div className="flex w-full min-w-0 max-w-xl items-center justify-between gap-2 pl-8 pb-6">
                <div className="flex items-center gap-2">
                  {/* <Select
                      value={groupedBy}
                      onValueChange={(value) => {
                        setGroupedBy(value as "day" | "week");
                      }}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">By Day</SelectItem>
                        <SelectItem value="week">By Week</SelectItem>
                      </SelectContent>
                    </Select> */}
                  <Select
                    value={isStacked ? "stacked" : "normal"}
                    onValueChange={(value) => {
                      setIsStacked(value === "stacked");
                    }}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stacked">Total</SelectItem>
                      <SelectItem value="normal">Split</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Legend
                  categories={["Billable Hours", "Non-Billable Hours"]}
                  colors={["purple", "gray"]}
                />
              </div>
              <div
                className={clsx(
                  groupedBy === "day" ? "max-w-3xl" : "mx-auto max-w-sm"
                )}
              >
                <BarChart
                  // key={index}
                  // type="stacked"
                  // xAxisLabel="Days"
                  allowDecimals={false}
                  // enableLegendSlider
                  // yAxisLabel="Hours"
                  stack={isStacked}
                  className="h-44 w-full"
                  data={time}
                  index="day"
                  categories={["Billable Hours", "Non-Billable Hours"]}
                  colors={["purple", "gray"]}
                  showLegend={false}
                  valueFormatter={(value) => {
                    return convertMsToNatural(value * 60 * 60 * 1000);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        {!!timesheetEntries ? (
          <>
            {timesheetEntries.length > 0 ? (
              <div>
                <div className="flex h-14 items-center border-b border-gray-200/70 px-8">
                  <div className="flex items-center gap-2">
                    {/* <Button variant={"secondary"}>Filter</Button> */}
                    {/* <Button variant={"secondary"}>Display</Button> */}
                    <div className="text-sm">Entries</div>
                  </div>
                </div>
                {/* <div className="flex h-14 items-center border-b border-gray-200/70 px-8">
              <div className="flex items-center gap-2">
              <Select
                value={entriesGroupedBy}
                onValueChange={(value) => {
                  setEntriesGroupedBy(value as "none" | "project");
                }}
              >
                <SelectTrigger className="w-52 py-0 items-center">
                  <div className="flex items-center gap-2 h-full">
                    <div className="border-r pr-2 h-full inline-flex items-center">Group By</div>
                    <div className="flex justify-start">
                      <SelectValue />
                    </div>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                </SelectContent>
              </Select>
              </div>
            </div> */}
                <EntriesTable
                  week={dayjs(daysOfWeek[0]).format("YYYY-MM-DD")}
                />
              </div>
            ) : (
              <div className="mt-10 flex items-center justify-center text-gray-600">
                No time entries added
              </div>
            )}
          </>
        ) : (
          <Loading className="mx-auto mt-10" />
        )}
      </div>
    </>
  );
}

type SelectedEvent = {
  _id: string;
  project: ProjectData | undefined;
  stage: ProjectData["stages"][number] | undefined;
  task: TaskData | undefined;
  date: string;
  time: number;
  billable: boolean;
  name: string;
};

export function AddEntriesFromCalendar({
  currentWeek,
  open,
  setOpen,
}: {
  currentWeek: Date;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const { data: calendarEvents } = useGetCalendarEvents(
    dayjs(getDateOfMonday(currentWeek)).format("YYYY-MM-DD")
  );
  const snackbarCtx = useSnackBar();
  // console.log(calendarEvents);
  const [selectedEntries, setSelectedEntries] = useState<SelectedEvent[]>([]);
  // const calendarEvents: CalendarEventData[] = [
  //   {
  //     id: "1",
  //     duration: 1000 * 60 * 60 * 8,
  //     title: "This is some task",
  //     summary: "Project 1",
  //     start: dayjs("2024-09-11T09:00:00").toISOString(),
  //     end: dayjs("2024-09-11T17:00:00").toISOString(),
  //   },
  //   {
  //     id: "4",
  //     duration: 1000 * 60 * 60 * 2,
  //     title: "This task",
  //     summary: "Project 1",
  //     start: dayjs("2024-09-11T15:00:00").toISOString(),
  //     end: dayjs("2024-09-11T17:00:00").toISOString(),
  //   },
  //   {
  //     id: "2",
  //     duration: 1000 * 60 * 60 * 8,
  //     title: "This is another task with a much longer description",
  //     summary: "Project 2",
  //     start: dayjs("2024-09-12T09:00:00").toISOString(),
  //     end: dayjs("2024-09-12T17:00:00").toISOString(),
  //   },
  //   {
  //     id: "3",
  //     duration: 1000 * 60 * 60 * 2,
  //     title: "This is an ok task",
  //     summary: "Project 3",
  //     start: dayjs("2024-09-13T09:00:00").toISOString(),
  //     end: dayjs("2024-09-13T11:00:00").toISOString(),
  //   },
  // ];

  const groupedCalendarEvents: Record<string, CalendarEventData[]> =
    useMemo(() => {
      let gce: Record<string, CalendarEventData[]> = {};
      // iterate through days of the week
      for (let i = 0; i < 7; i++) {
        const day = dayjs(getDateOfMonday(currentWeek)).add(i, "day");
        gce[day.format("YYYY-MM-DD")] = [];
      }
      // iterate through calendar events and add them to the correct day
      calendarEvents?.forEach((event) => {
        const eventDate = dayjs(event.start).format("YYYY-MM-DD");
        if (gce[eventDate]) {
          gce[eventDate].push(event);
        }
      });
      return gce;
    }, [calendarEvents]);

  const groupedSelectedEvents: Record<string, SelectedEvent[]> = useMemo(() => {
    let gse: Record<string, SelectedEvent[]> = {};
    // iterate through days of the week
    for (let i = 0; i < 7; i++) {
      const day = dayjs(getDateOfMonday(currentWeek)).add(i, "day");
      gse[day.format("YYYY-MM-DD")] = [];
    }
    // iterate through calendar events and add them to the correct day
    selectedEntries?.forEach((event) => {
      const eventDate = dayjs(event.date).format("YYYY-MM-DD");
      if (gse[eventDate]) {
        gse[eventDate].push(event);
      }
    });
    return gse;
  }, [selectedEntries]);
  const [multiSelectedIds, setMultiSelectedIds] = useState<string[]>([]);
  const hasEventsThisWeek = useMemo(() => {
    if (!calendarEvents) return false;
    return calendarEvents?.some((event) => {
      const eventStart = dayjs(event.start);
      const weekStart = dayjs(getDateOfMonday(currentWeek));
      const weekEnd = weekStart.add(6, "day");
      return eventStart.isBetween(weekStart, weekEnd, "day", "[]");
    });
  }, [calendarEvents]);

  const [step, setStep] = useState(0);
  // const [open, setOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const { data: projects } = useGetProjects();
  // const createTimesheetRows = useCreateTimesheetRows(
  //   dayjs(getDateOfMonday(currentWeek)).format("YYYY-MM-DD")
  // );
  const createTimeEntries = useCreateTimeEntries();
  const { data: settings } = useGetUserSettings();
  const app = useRealmApp();
  useEffect(() => {
    if (createTimeEntries.isSuccess) {
      setOpen(false);
      setSelectedEntries([]);
      setStep(0);
      setMultiSelectedIds([]);
    }
  }, [createTimeEntries.isSuccess]);

  const navigate = useNavigate();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className={clsx(
          "flex flex-col gap-0 p-0",
          hasEventsThisWeek && "h-[calc(100vh-10rem)]",
          step === 0 && "max-w-4xl",
          step === 1 && "max-w-6xl"
        )}
      >
        {!!settings?.calendarIntegration ? (
          <>
            <div
              className={clsx(
                "relative flex-1 overflow-auto",
                hasEventsThisWeek ? "pb-12" : "pb-8"
              )}
            >
              <DialogHeader className="p-8 pb-3">
                <DialogTitle>Add Calendar Events</DialogTitle>
              </DialogHeader>

              {!calendarEvents && <Loading className="mx-auto my-4" />}
              {step === 0 && (
                <>
                  {!!calendarEvents && (
                    <>
                      {hasEventsThisWeek ? (
                        <div>
                          <div className="px-8 pb-10 pt-4 text-gray-800">
                            Select the calendar events you want to convert to
                            time entries.
                          </div>
                          <div className="space-y-10">
                            {Object.keys(groupedCalendarEvents).map((date) => {
                              return (
                                <InitialDayEvents
                                  date={date}
                                  events={groupedCalendarEvents[date]}
                                  selectedEntries={selectedEntries}
                                  setSelectedEntries={setSelectedEntries}
                                  // calendarEvents={calendarEvents}
                                />
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="px-8 pt-2 text-gray-800">
                          You have no calendar events for this week.
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
              {step === 1 && (
                <>
                  {!!calendarEvents && (
                    <>
                      {hasEventsThisWeek ? (
                        <div>
                          <div className="px-8 pb-10 pt-4 text-gray-800">
                            Assign projects to the selected calendar events.
                          </div>
                          <div className="space-y-10">
                            {Object.keys(groupedSelectedEvents).map((date) => {
                              return (
                                <DayEvents
                                  date={date}
                                  events={groupedSelectedEvents[date]}
                                  multiSelectedIds={multiSelectedIds}
                                  setMultiSelectedIds={setMultiSelectedIds}
                                  // selectedEntries={selectedEntries}
                                  setSelectedEntries={setSelectedEntries}

                                  // calendarEvents={calendarEvents}
                                />
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="px-8 pt-2 text-gray-800">
                          You have no calendar events for this week.
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            {!!multiSelectedIds.length && step === 1 && (
              <div className="flex h-14 w-full items-center space-x-2 border-t border-gray-200 bg-white px-8">
                <div className="w-20 text-sm text-gray-500">
                  {multiSelectedIds.length} selected
                </div>
                <div className="w-40">
                  <Popover open={projectOpen} onOpenChange={setProjectOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="secondary"
                        className="w-full justify-between space-x-2 font-normal"
                      >
                        <div className="flex space-x-1 text-gray-400">
                          Project
                          {/* <span className="top-0 ml-0.5 text-primary-main">*</span> */}
                          {/* {required && (
                        )} */}
                        </div>
                        <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="pointer-events-auto p-0"
                      align="start"
                    >
                      <Command>
                        <CommandInput placeholder="Search projects..." />
                        <CommandList>
                          <CommandEmpty>No project found.</CommandEmpty>
                          <CommandGroup>
                            {projects?.map((project) => (
                              <CommandItem
                                key={project._id}
                                onSelect={() => {
                                  setSelectedEntries((prev) =>
                                    prev.map((entry) => {
                                      if (
                                        multiSelectedIds.includes(entry._id)
                                      ) {
                                        return {
                                          ...entry,
                                          project: project,
                                          stage: undefined,
                                          task: undefined,
                                          // projectId: project._id,
                                        };
                                      }
                                      return entry;
                                    })
                                  );
                                  setMultiSelectedIds([]);
                                  setProjectOpen(false);
                                  // createTimesheetRow.mutate({
                                  //   phaseId: project._id,
                                  // });
                                }}
                                className="max-w-full truncate"
                              >
                                {project.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="w-40">
                  <Select
                    onValueChange={(value) => {
                      setSelectedEntries((prev) =>
                        prev.map((entry) => {
                          if (multiSelectedIds.includes(entry._id)) {
                            return {
                              ...entry,
                              billable: value === "billable",
                            };
                          }
                          return entry;
                        })
                      );
                      setMultiSelectedIds([]);
                    }}
                  >
                    <SelectTrigger>
                      <div className="text-gray-400">Billable</div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="billable">Yes</SelectItem>
                      <SelectItem value="non-billable">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {hasEventsThisWeek && (
              <>
                {step === 0 && (
                  <>
                    <div className="flex shrink-0 justify-between border-t border-gray-200 px-8 py-5">
                      <div>
                        <div className="text-sm text-gray-500">
                          {selectedEntries.length}{" "}
                          {selectedEntries.length === 1 ? "event" : "events"}{" "}
                          selected (
                          {convertMsToNatural(
                            selectedEntries.reduce(
                              (acc, entry) => acc + entry.time,
                              0
                            ),
                            true
                          )}
                          )
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant={"secondary"}
                          onClick={() => setOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          disabled={selectedEntries.length === 0}
                          onClick={() => setStep(1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
                {step === 1 && (
                  <>
                    <div className="flex shrink-0 justify-between border-t border-gray-200 px-8 py-5">
                      <div>
                        {/* <div className="text-sm text-gray-500">
                      {selectedEntries.length}{" "}
                      {selectedEntries.length === 1 ? "event" : "events"}{" "}
                      selected (
                      {convertMsToNatural(
                        selectedEntries.reduce(
                          (acc, entry) => acc + entry.time,
                          0
                        ),
                        true
                      )}
                      )
                    </div> */}
                        <Button
                          variant={"secondary"}
                          onClick={() => setStep(0)}
                        >
                          Back
                        </Button>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant={"secondary"}
                          onClick={() => setOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          disabled={createTimeEntries.isLoading}
                          onClick={() => {
                            const entriesWithNoProject = selectedEntries.filter(
                              (entry) => !entry.project
                            );
                            if (entriesWithNoProject.length > 0) {
                              snackbarCtx.showSnackbar(
                                "Every calendar event must be assigned a project.",
                                "error"
                              );
                            } else {
                              createTimeEntries.mutate({
                                entries: selectedEntries.map(
                                  (selectedEntry) => {
                                    return {
                                      // week: dayjs(
                                      //   getDateOfMonday(currentWeek)
                                      // ).format("YYYY-MM-DD"),
                                      projectId: String(
                                        selectedEntry.project?._id
                                      ),
                                      date: dayjs(selectedEntry.date).format(
                                        "YYYY-MM-DD"
                                      ),
                                      time: selectedEntry.time,
                                      userId: app.currentUser?.id,
                                      description: selectedEntry.name,
                                      isBillable: selectedEntry.billable,
                                      stageId: selectedEntry.stage?._id
                                        ? String(selectedEntry.stage?._id)
                                        : undefined,
                                      taskId: selectedEntry.task?._id
                                        ? String(selectedEntry.task?._id)
                                        : undefined,
                                      // entries: [
                                      //   {
                                      //     date: dayjs(selectedEntry.date).format(
                                      //       "YYYY-MM-DD"
                                      //     ),
                                      //     time: selectedEntry.time,
                                      //     isBillable: selectedEntry.billable,
                                      //     description: "",
                                      //     // categoryId: selectedEntry.categoryId,
                                      //   },
                                      // ],
                                    };
                                  }
                                ),
                              });
                            }
                          }}
                        >
                          {createTimeEntries.isLoading
                            ? "Creating..."
                            : "Create Time Entries"}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <div className="p-6">
            <DialogHeader>
              <DialogTitle>Add Calendar Events</DialogTitle>
            </DialogHeader>
            <div className="pt-4 text-gray-700">
              Your Google Calendar is not connected. Click{" "}
              <span
                className="cursor-pointer text-black hover:underline"
                onClick={() => {
                  navigate("/settings/integrations/google-calendar");
                }}
              >
                here
              </span>{" "}
              to go to the Google Calendar settings.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function InitialDayEvents({
  date,
  events,
  selectedEntries,
  setSelectedEntries,
}: // calendarEvents,
{
  date: string;
  events: CalendarEventData[];
  selectedEntries: SelectedEvent[];
  setSelectedEntries: (entries: SelectedEvent[]) => void;
  // calendarEvents: CalendarEventData[];
}) {
  if (!events.length) return null;
  return (
    <div>
      <div className="mx-8 mb-1 flex items-center gap-3 border-b border-gray-200 pb-2 text-sm text-gray-500">
        {/* <div className="w-4" /> */}
        <div className="flex-1 truncate pr-2">
          Event on {dayjs(date).format("ddd, MMM DD")}
        </div>
        {/* <div className="w-28">Date</div> */}
        <div className="w-24">Duration</div>
        {/* <div className="w-24">Selected</div> */}
        <div className="w-12" />
        {/* <div className="w-40">Category</div> */}
      </div>
      <div className="space-y-0.5">
        {events
          // .filter((e) => dayjs(e.start).format("YYYY-MM-DD") === date)
          .map((event) => (
            <InitialEvent
              event={event}
              selectedEntries={selectedEntries}
              setSelectedEntries={setSelectedEntries}
            />
          ))}
      </div>
    </div>
  );
}

function InitialEvent({
  event,
  selectedEntries,
  setSelectedEntries,
}: {
  event: CalendarEventData;
  selectedEntries: SelectedEvent[];
  setSelectedEntries: (entries: SelectedEvent[]) => void;
}) {
  const { data: projects } = useGetProjects();
  const selectedEntry = selectedEntries.find((entry) => entry._id === event.id);
  // const project = projects?.find(
  //   (project) => project._id === selectedEntry?.projectId
  // );
  // const [projectOpen, setProjectOpen] = useState(false);
  return (
    <div
      key={event.id}
      className={clsx(
        "group mx-6 flex h-9 items-center gap-3 rounded-md px-2 pr-1 text-sm",
        !!selectedEntry ? "bg-primary/10" : ""
      )}
    >
      <div className="flex-1 cursor-default truncate pr-2">{event.title}</div>
      <div className="w-24 cursor-default">
        {convertMsToNatural(event.duration, true)}
      </div>
      {/* <div className="w-24">
        <Checkbox
          className={clsx(!!selectedEntry ? "" : "hover:bg-gray-100")}
          checked={!!selectedEntry}
          onClick={() => {
            const checked = !!selectedEntry;
            if (!checked) {
              setSelectedEntries([
                ...selectedEntries,
                {
                  _id: event.id,
                  projectId: undefined,
                  date: event.start,
                  time: event.duration,
                  billable: true,
                },
              ]);
            } else {
              setSelectedEntries(
                selectedEntries.filter((entry) => entry._id !== event.id)
              );
            }
          }}
        />
      </div> */}
      <div className="flex w-12 justify-end">
        {/* <Switch /> */}
        <Button
          variant={"secondary"}
          size="sm"
          onClick={() => {
            const checked = !!selectedEntry;
            if (!checked) {
              setSelectedEntries([
                ...selectedEntries,
                {
                  _id: event.id,
                  project: undefined,
                  date: event.start,
                  time: event.duration,
                  billable: true,
                  name: event.title,
                  stage: undefined,
                  task: undefined,
                },
              ]);
            } else {
              setSelectedEntries(
                selectedEntries.filter((entry) => entry._id !== event.id)
              );
            }
          }}
        >
          <PlusIcon
            className={clsx(
              "h-4 w-4 transition-transform",
              !!selectedEntry ? "rotate-45" : ""
            )}
          />
          {/* {!!selectedEntry ? (
          ) : (
            <PlusIcon className="h-4 w-4" />
          )} */}
        </Button>
        {/* <Checkbox
          className={clsx(!!selectedEntry ? "" : "hover:bg-gray-100")}
          checked={!!selectedEntry}
          onClick={() => {
            const checked = !!selectedEntry;
            if (!checked) {
              setSelectedEntries([
                ...selectedEntries,
                {
                  _id: event.id,
                  projectId: undefined,
                  date: event.start,
                  time: event.duration,
                  billable: true,
                },
              ]);
            } else {
              setSelectedEntries(
                selectedEntries.filter((entry) => entry._id !== event.id)
              );
            }
          }}
        /> */}
      </div>
      {/* <div className="w-40">
    {!!selectedEntries.find((entry) => entry._id === event.id) ? (
      <Select
        disabled={
          !selectedEntries.find((entry) => entry._id === event.id)
        }
      >
        <SelectTrigger
          className="disabled:opacity-40"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <SelectValue placeholder={"Select a category"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="1">Project 1</SelectItem>
          <SelectItem value="2">Project 2</SelectItem>
        </SelectContent>
      </Select>
    ) : (
      <div className="h-8 w-full rounded-md border border-dashed" />
    )}
  </div> */}
    </div>
  );
}

function DayEvents({
  date,
  events,
  setSelectedEntries,
  multiSelectedIds,
  setMultiSelectedIds,
}: // calendarEvents,
{
  date: string;
  events: SelectedEvent[];
  // selectedEntries: {
  //   _id: string;
  //   projectId?: string;
  //   date: string;
  //   time: number;
  //   billable: boolean;
  // }[];
  setSelectedEntries: React.Dispatch<React.SetStateAction<SelectedEvent[]>>;
  multiSelectedIds: string[];
  setMultiSelectedIds: (ids: string[]) => void;
  // calendarEvents: CalendarEventData[];
}) {
  if (!events.length) return null;
  return (
    <div>
      <div className="mx-8 mb-1 flex items-center gap-3 border-b border-gray-200 pb-2 text-sm text-gray-500">
        {/* <div className="w-4" /> */}

        <div className="flex-1 truncate pr-2">
          Description for event on {dayjs(date).format("ddd, MMM DD")}
        </div>
        {/* <div className="w-28">Date</div> */}
        <div className="w-24">Duration</div>
        <div className="w-24">Billable</div>
        <div className="w-40">Project</div>
        <div className="w-40">Stage</div>
        <div className="w-40">Task</div>
        {/* <div className="w-40">Category</div> */}
      </div>
      <div className="space-y-1.5 pt-1">
        {events
          // .filter((e) => dayjs(e.start).format("YYYY-MM-DD") === date)
          .map((event) => (
            <Event
              event={event}
              // selectedEntries={selectedEntries}
              setSelectedEntries={setSelectedEntries}
              multiSelectedIds={multiSelectedIds}
              setMultiSelectedIds={setMultiSelectedIds}
            />
          ))}
      </div>
    </div>
  );
}

function Event({
  event,
  // selectedEntries,
  setSelectedEntries,
  multiSelectedIds,
  setMultiSelectedIds,
}: {
  event: SelectedEvent;
  // selectedEntries: {
  //   _id: string;
  //   projectId?: string;
  //   date: string;
  //   time: number;
  //   billable: boolean;
  // }[];
  setSelectedEntries: React.Dispatch<React.SetStateAction<SelectedEvent[]>>;
  multiSelectedIds: string[];
  setMultiSelectedIds: (ids: string[]) => void;
}) {
  const { data: projects } = useGetProjects();
  // const selectedEntry = selectedEntries.find((entry) => entry._id === event.id);
  // const project = projects?.find((project) => project._id === event.projectId);
  const [projectOpen, setProjectOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  // if (!selectedEntry) return null;
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const adjustHeight = () => {
      if (textAreaRef.current) {
        textAreaRef.current.style.height = "32px"; // Reset height to shrink if needed
        const scrollHeight = textAreaRef.current.scrollHeight;
        textAreaRef.current.style.height = scrollHeight + "px";
      }
    };
    adjustHeight();
    setTimeout(adjustHeight, 50);
  }, [event.name]);

  return (
    <div
      key={event._id}
      className="group mx-6 flex items-start gap-3 rounded-md px-2 text-sm"
    >
      <div className="w-4 pt-[7px]">
        <Checkbox
          className={clsx(
            multiSelectedIds.includes(event._id) ? "" : "hover:bg-gray-100"
          )}
          checked={multiSelectedIds.includes(event._id)}
          onClick={() => {
            const checked = multiSelectedIds.includes(event._id);
            if (!checked) {
              setMultiSelectedIds([...multiSelectedIds, event._id]);
            } else {
              setMultiSelectedIds(
                multiSelectedIds.filter((id) => id !== event._id)
              );
            }
          }}
          // onClick={() => {
          //   const checked = multiSelectedIds.includes(event._id);
          //   if (!checked) {
          //     setSelectedEntries([
          //       ...selectedEntries,
          //       {
          //         _id: event.id,
          //         projectId: undefined,
          //         date: event.start,
          //         time: event.duration,
          //         billable: true,
          //       },
          //     ]);
          //   } else {
          //     setSelectedEntries(
          //       selectedEntries.filter((entry) => entry._id !== event.id)
          //     );
          //   }
          // }}
        />
      </div>
      <div className="flex flex-1 cursor-default items-center pr-2">
        <textarea
          ref={textAreaRef}
          className="h-[32px] w-full resize-none rounded-md border border-input bg-transparent py-[6px] px-2 text-sm focus:border-primary focus:ring-[0.5px] focus:ring-primary"
          rows={1}
          value={event.name}
          onChange={(e) => {
            setSelectedEntries((selectedEntries) =>
              selectedEntries.map((entry) => {
                if (entry._id === event._id) {
                  return { ...entry, name: e.target.value };
                }
                return entry;
              })
            );
          }}
        />
      </div>
      <div className="w-24 cursor-default pt-[7px]">
        {convertMsToNatural(event.time, true)}
      </div>
      <div className="w-24 cursor-default pt-[7px]">
        <SecondaryCheckbox
          onClick={(e) => {
            e.stopPropagation();
            setSelectedEntries((selectedEntries) =>
              selectedEntries.map((entry) => {
                if (entry._id === event._id) {
                  return {
                    ...entry,
                    billable: !entry.billable,
                  };
                }
                return entry;
              })
            );
          }}
          checked={event.billable}
        />
        {/* ) : (
          <div className="h-4 w-4 rounded border border-dashed border-gray-300/70" />
        )} */}
      </div>
      <div className="w-40">
        {/* {!!selectedEntry ? ( */}
        <Popover open={projectOpen} onOpenChange={setProjectOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              className="w-full justify-between space-x-2 font-normal"
            >
              {/* <UserIcon viewBox="4 0 20 24" className="h-3.5 shrink-0" /> */}
              {/* {!!selectedTasker && (
                <div
                  className={clsx(
                    "h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300",
                    selectedTasker.type === "internal"
                      ? "bg-gray-300"
                      : "bg-slate-700"
                  )}
                />
              )} */}
              {event.project ? (
                <div className="truncate">{event.project.name}</div>
              ) : (
                <div className="flex space-x-1 text-gray-400/80">
                  Project
                  <span className="top-0 ml-0.5 text-primary-main">*</span>
                </div>
              )}
              <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="pointer-events-auto p-0" align="start">
            <Command>
              <CommandInput placeholder="Search projects..." />

              <CommandList>
                <CommandEmpty>No project found.</CommandEmpty>
                <CommandGroup>
                  {projects?.map((project) => (
                    <CommandItem
                      key={project._id}
                      onSelect={() => {
                        setSelectedEntries((selectedEntries) =>
                          selectedEntries.map((entry) => {
                            if (entry._id === event._id) {
                              return {
                                ...entry,
                                project: project,
                                stage: undefined,
                                task: undefined,
                              };
                            }
                            return entry;
                          })
                        );
                        setProjectOpen(false);
                        // createTimesheetRow.mutate({
                        //   phaseId: project._id,
                        // });
                      }}
                      className="truncate"
                    >
                      <span className="truncate">{project.name}</span>
                      <div className="flex flex-1 shrink-0 items-center justify-end space-x-1">
                        {event.project?._id === project._id ? (
                          <div
                            className="cursor-pointer rounded-full p-1 transition-all hover:bg-gray-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEntries((selectedEntries) =>
                                selectedEntries.map((entry) => {
                                  if (entry._id === event._id) {
                                    return {
                                      ...entry,
                                      project: undefined,
                                    };
                                  }
                                  return entry;
                                })
                              );
                              setProjectOpen(false);
                            }}
                          >
                            <XMarkIcon
                              className={clsx(
                                "h-2.5 w-2.5 shrink-0",
                                event.project?._id === project._id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          </div>
                        ) : (
                          <div className="w-5" />
                        )}
                        <CheckIcon
                          className={clsx(
                            "h-4 w-4 shrink-0",
                            event.project?._id === project._id
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                      </div>
                      {/* {project.name} */}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {/* ) : (
          <div className="h-8 w-full rounded-md border border-dashed" />
        )} */}
      </div>
      <div className="w-40">
        {/* {!!selectedEntry ? ( */}
        {event.project && (
          <Popover open={stageOpen} onOpenChange={setStageOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="secondary"
                className="w-full justify-between space-x-2 font-normal"
              >
                {/* <UserIcon viewBox="4 0 20 24" className="h-3.5 shrink-0" /> */}
                {/* {!!selectedTasker && (
                <div
                  className={clsx(
                    "h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300",
                    selectedTasker.type === "internal"
                      ? "bg-gray-300"
                      : "bg-slate-700"
                  )}
                />
              )} */}
                {event.stage ? (
                  <div className="truncate">{event.stage.name}</div>
                ) : (
                  <div className="flex space-x-1 text-gray-400/80">
                    Stage
                    {/* <span className="top-0 ml-0.5 text-primary-main">*</span> */}
                  </div>
                )}
                <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="pointer-events-auto p-0" align="start">
              <Command>
                <CommandInput placeholder="Search stages..." />

                <CommandList>
                  <CommandEmpty>No stage found.</CommandEmpty>
                  <CommandGroup>
                    {event.project?.stages?.map((stage) => (
                      <CommandItem
                        key={stage._id}
                        onSelect={() => {
                          setSelectedEntries((selectedEntries) =>
                            selectedEntries.map((entry) => {
                              if (entry._id === event._id) {
                                return {
                                  ...entry,
                                  stage: stage,
                                  task: undefined,
                                };
                              }
                              return entry;
                            })
                          );
                          setStageOpen(false);
                          // createTimesheetRow.mutate({
                          //   phaseId: project._id,
                          // });
                        }}
                        className="max-w-full truncate"
                      >
                        <span className="truncate">{stage.name}</span>
                        <div className="flex flex-1 shrink-0 items-center justify-end space-x-1">
                          {event.stage?._id === stage._id ? (
                            <div
                              className="cursor-pointer rounded-full p-1 transition-all hover:bg-gray-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEntries((selectedEntries) =>
                                  selectedEntries.map((entry) => {
                                    if (entry._id === event._id) {
                                      return {
                                        ...entry,
                                        stage: undefined,
                                      };
                                    }
                                    return entry;
                                  })
                                );
                                setStageOpen(false);
                              }}
                            >
                              <XMarkIcon
                                className={clsx(
                                  "h-2.5 w-2.5 shrink-0",
                                  event.stage?._id === stage._id
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                            </div>
                          ) : (
                            <div className="w-5" />
                          )}
                          <CheckIcon
                            className={clsx(
                              "ml-auto h-4 w-4 shrink-0",
                              event.stage?._id === stage._id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
        {/* ) : (
          <div className="h-8 w-full rounded-md border border-dashed" />
        )} */}
      </div>
      <TasksComboboxWrapper
        event={event}
        setSelectedEntries={setSelectedEntries}
      />
      {/* <div className="w-40">
    {!!selectedEntries.find((entry) => entry._id === event.id) ? (
      <Select
        disabled={
          !selectedEntries.find((entry) => entry._id === event.id)
        }
      >
        <SelectTrigger
          className="disabled:opacity-40"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <SelectValue placeholder={"Select a category"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="1">Project 1</SelectItem>
          <SelectItem value="2">Project 2</SelectItem>
        </SelectContent>
      </Select>
    ) : (
      <div className="h-8 w-full rounded-md border border-dashed" />
    )}
  </div> */}
    </div>
  );
}

function TasksComboboxWrapper({
  event,
  setSelectedEntries,
}: {
  event: SelectedEvent;
  setSelectedEntries: React.Dispatch<React.SetStateAction<SelectedEvent[]>>;
}) {
  if (!event.project?._id) return <div className="w-40" />;
  return (
    <TasksCombobox event={event} setSelectedEntries={setSelectedEntries} />
  );
}

function TasksCombobox({
  event,
  setSelectedEntries,
}: {
  event: SelectedEvent;
  setSelectedEntries: React.Dispatch<React.SetStateAction<SelectedEvent[]>>;
}) {
  const [taskOpen, setTaskOpen] = useState(false);
  const { data: tasks } = useGetPhaseTasks(String(event.project?._id));
  return (
    <div className="w-40">
      {/* {!!selectedEntry ? ( */}
      <Popover open={taskOpen} onOpenChange={setTaskOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            className="w-full justify-between space-x-2 font-normal"
          >
            {/* <UserIcon viewBox="4 0 20 24" className="h-3.5 shrink-0" /> */}
            {/* {!!selectedTasker && (
            <div
              className={clsx(
                "h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300",
                selectedTasker.type === "internal"
                  ? "bg-gray-300"
                  : "bg-slate-700"
              )}
            />
          )} */}
            {event.task ? (
              <div className="truncate">{event.task.title}</div>
            ) : (
              <div className="flex space-x-1 text-gray-400/80">
                Task
                {/* <span className="top-0 ml-0.5 text-primary-main">*</span> */}
                {/* {required && (
                )} */}
              </div>
            )}
            <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="pointer-events-auto p-0" align="end">
          <Command>
            <CommandInput placeholder="Search tasks..." />

            <CommandList>
              <CommandEmpty>No task found.</CommandEmpty>
              {event.project?.stages
                .filter((s) => {
                  if (!!event.stage?._id) {
                    if (event.stage._id === s._id) {
                      return true;
                    }
                    return false;
                  } else {
                    return true;
                  }
                })
                .map((s) => (
                  <CommandGroup heading={s.name}>
                    {tasks
                      ?.filter((t) => t.journeyStage?._id === s._id)
                      .map((task) => (
                        <CommandItem
                          key={task._id}
                          onSelect={() => {
                            setSelectedEntries((selectedEntries) =>
                              selectedEntries.map((entry) => {
                                if (entry._id === event._id) {
                                  return {
                                    ...entry,
                                    task: task,
                                    stage: entry.project?.stages.find(
                                      (s) => s._id === task.journeyStage?._id
                                    ),
                                  };
                                }
                                return entry;
                              })
                            );
                            setTaskOpen(false);
                            // createTimesheetRow.mutate({
                            //   phaseId: project._id,
                            // });
                          }}
                          className="gap-2"
                        >
                          <TooltipProvider>
                            <Tooltip delayDuration={0}>
                              <TooltipTrigger>
                                <div
                                  className={clsx(
                                    "h-2 w-2 shrink-0 rounded-full",
                                    task.status === "To-do" && "bg-amber-300",
                                    task.status === "In Progress" &&
                                      "bg-blue-400",
                                    task.status === "Completed" &&
                                      "bg-green-400",
                                    task.status === "Canceled" && "bg-gray-400",
                                    task.status === "Stuck" && "bg-red-400",
                                    task.status === "Backlog" && "bg-gray-200"
                                  )}
                                />
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                {task.status}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <span className="truncate">{task.title}</span>
                          <div className="flex flex-1 shrink-0 items-center justify-end space-x-1">
                            {event.task?._id === task._id ? (
                              <div
                                className="cursor-pointer rounded-full p-1 transition-all hover:bg-gray-200"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEntries((selectedEntries) =>
                                    selectedEntries.map((entry) => {
                                      if (entry._id === event._id) {
                                        return {
                                          ...entry,
                                          task: undefined,
                                        };
                                      }
                                      return entry;
                                    })
                                  );
                                  setTaskOpen(false);
                                }}
                              >
                                <XMarkIcon
                                  className={clsx(
                                    "h-2.5 w-2.5 shrink-0",
                                    event.task?._id === task._id
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </div>
                            ) : (
                              <div className="w-5" />
                            )}
                            <CheckIcon
                              className={clsx(
                                "h-4 w-4 shrink-0",
                                event.task?._id === task._id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          </div>
                        </CommandItem>
                      ))}
                  </CommandGroup>
                ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {/* ) : (
      <div className="h-8 w-full rounded-md border border-dashed" />
    )} */}
    </div>
  );
}

function getColumns(): ColumnDef<TimeEntryData>[] {
  return [
    {
      id: "project",
      header: () => {
        return <div className="">Project</div>;
      },
      cell: ({ row }) => {
        return (
          <div className="h-full max-w-[400px] truncate">
            {row.original.project.name}
          </div>
        );
      },
      accessorFn: ({ project }) => project.name,
    },
    {
      id: "stage",
      header: () => {
        return <div className="">Stage</div>;
      },
      accessorFn: ({ stage }) => stage?.name,
      cell: ({ row }) => {
        return (
          <div className="h-full max-w-[200px] truncate">
            {row.original.stage?.name}
          </div>
        );
      },
    },
    {
      id: "task",
      header: () => {
        return <div className="">Task</div>;
      },
      accessorFn: ({ task }) => task?.title,
      cell: ({ row }) => {
        return (
          <div className="h-full max-w-[200px] truncate">
            {row.original.task?.title}
          </div>
        );
      },
    },
    {
      id: "description",
      header: () => {
        return <div className="">Description</div>;
      },
      accessorFn: ({ description }) => description,
      cell: ({ row }) => {
        return (
          <div className="h-full w-[240px] whitespace-normal">
            {row.original.description}
          </div>
        );
      },
    },
    {
      id: "date",
      header: () => {
        return <div className="">Date</div>;
      },
      accessorFn: ({ date }) => date,
      cell: ({ row }) => {
        return (
          <div className="h-full w-32 whitespace-normal">
            {dayjs(row.original.date).format("ddd, MMM DD")}
          </div>
        );
      },
    },
    // {
    //   id: "status",
    //   header: () => {
    //     return <div className="">Status</div>;
    //   },
    //   accessorFn: ({ status }) => status,
    //   cell: ({ row }) => {
    //     return (
    //       <>
    //         {row.original.status === "pending" && (
    //           <div className="flex items-center space-x-2">
    //             <div className="h-2 w-2 rounded-full bg-amber-400" />
    //             <div>Pending</div>
    //           </div>
    //         )}
    //         {row.original.status === "approved" && (
    //           <div className="flex items-center space-x-2">
    //             <div className="h-2 w-2 rounded-full bg-green-400" />
    //             <div>Approved</div>
    //           </div>
    //         )}
    //         {row.original.status === "rejected" && (
    //           <div className="flex items-center space-x-2">
    //             <div className="h-2 w-2 rounded-full bg-red-400" />
    //             <div>Rejected</div>
    //           </div>
    //         )}
    //       </>
    //     );
    //   },
    // },
    {
      id: "billable",
      header: () => {
        return <div className="">Billable</div>;
      },
      accessorFn: ({ time }) => time,
      cell: ({ row }) => {
        return (
          <div className="h-full truncate">
            {row.original.isBillable ? (
              <div className="flex h-6 w-12 items-center justify-center rounded-md bg-purple-200/70">
                Yes
              </div>
            ) : (
              <div className="flex h-6 w-12 items-center justify-center rounded-md bg-gray-100">
                No
              </div>
            )}
          </div>
        );
      },
    },
    // {
    //   id: "billable",
    //   header: () => {
    //     return <div className="">Billable</div>;
    //   },
    //   accessorFn: ({ time }) => time,
    //   cell: ({ row }) => {
    //     return (
    //       <div className="h-full truncate">
    //         {row.original.isBillable ? "Yes" : "No"}
    //       </div>
    //     );
    //   },
    // },
    {
      id: "time",
      header: () => {
        return <div className="">Time</div>;
      },
      accessorFn: ({ time }) => time,
      cell: ({ row }) => {
        return (
          <div className="h-full truncate">
            {convertMsToNatural(row.original.time)}
          </div>
        );
      },
    },
  ];
}

function EntriesTable({ week }: { week: string }) {
  const { data: timesheet } = useGetTimesheetEntriesForUser(week);

  // const timesheet: TimeEntryData[] = [
  //   {
  //     project: {
  //       _id: "1",
  //       name: "Project 1",
  //     },
  //     stage: {
  //       name: "Installation & Calibration",
  //       _id: "234",
  //     },
  //     task: {
  //       _id: "123",
  //       title: "This is a fairly long task name because on avg they are",
  //     },
  //     _id: "1",
  //     timesheetId: "1",
  //     description: "This is the description for this task",
  //     date: "2024-11-11",
  //     time: 1000 * 60 * 60 * 5,
  //     isBillable: true,
  //     instanceId: "1",
  //     userId: "1",
  //     createdAt: "2024-11-11",
  //     updatedAt: "2024-11-11",
  //     week: "2024-11-11",
  //   },
  // ];
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable<TimeEntryData>({
    data: timesheet ?? [],
    columns: getColumns(),
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      // globalFilter: search,
      // columnVisibility: transformColumnsToDisplay(columnsToDisplay),
    },
    // globalFilterFn: fuzzyFilter,
    onSortingChange: setSorting,
    // onGlobalFilterChange: setSearch,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // onColumnVisibilityChange: setColumnsToDisplay,
  });
  return (
    <div className="w-full flex-1 overflow-auto">
      <div className="max-h-full max-w-full overflow-auto">
        <table
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
          className="w-full"
        >
          <thead className="overflow-auto">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={
                      "sticky top-0 z-10 h-11 whitespace-nowrap border-b border-r border-gray-200/70 bg-gray-50 px-6 text-left text-xs font-normal uppercase tracking-wide text-gray-400 first:left-0 first:z-20 first:pl-8 last:right-0 last:z-20 last:border-l"
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {!timesheet ? (
              <tr>
                {/* <td className="inset-0 flex items-center justify-center p-4 pl-8"> */}
                <div className="sticky left-0 z-10 p-4 pl-8 text-gray-400">
                  Loading...
                </div>
                {/* </td> */}
              </tr>
            ) : (
              <>
                {timesheet.length === 0 ? (
                  <div className="flex w-full items-center justify-center">
                    No entries
                    {/* <div className="sticky left-0 z-10 whitespace-nowrap p-4 pl-8 text-gray-400">
                    </div> */}
                    {/* <td className="inset-0 flex items-center justify-center p-4">
                </td> */}
                  </div>
                ) : (
                  <>
                    {table.getRowModel().rows.map((row) => (
                      <EntryRow row={row} />
                    ))}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function EntryRow({ row }: { row: Row<TimeEntryData> }) {
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState(row.original.time);
  const [value, setValue] = useState(
    !time ? "" : convertMsToNatural(time || 0)
  );
  const [description, setDescription] = useState(row.original.description);
  const updateTimeEntry = useUpdateTimeEntry();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteTimeEntry = useDeleteTimesheetEntry();
  useEffect(() => {
    setDescription(row.original.description);
  }, [row.original.description]);

  useEffect(() => {
    setValue(!time ? "" : convertMsToNatural(time || 0));
  }, [time]);

  useEffect(() => {
    setTime(row.original.time);
  }, [row.original.time]);

  useEffect(() => {
    if (deleteTimeEntry.isSuccess) {
      setIsDeleting(false);
      setOpen(false);
    }
  }, [deleteTimeEntry.isSuccess]);

  return (
    <>
      <tr
        key={row.id}
        className="group relative rounded-lg hover:cursor-pointer hover:bg-gray-50"
        onClick={() => {
          setOpen(true);
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <td
            key={cell.id}
            className={
              "whitespace-nowrap border-r border-b border-gray-100 bg-white px-6 py-3 text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:bg-white first:pr-6 first:font-medium first:text-gray-900 last:sticky last:right-0 last:z-10 last:border-l group-hover:bg-gray-50 first:group-hover:bg-gray-50 first:sm:pl-8"
            }
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-fit">
          <DialogHeader>
            <DialogTitle>Edit Entry</DialogTitle>
          </DialogHeader>
          <div className="absolute top-[18px] right-12">
            <DropdownMenu
              open={optionsOpen}
              onOpenChange={(isOpen) => {
                setOptionsOpen(isOpen);
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant={"ghost"}
                  className={clsx(
                    "h-7 w-7 rounded p-0 hover:bg-gray-200/60 group-hover:visible",
                    !optionsOpen ? "bg-transparent" : "bg-gray-200/60"
                  )}
                >
                  <EllipsisHorizontalIcon className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-44" align="end">
                <DropdownMenuGroup>
                  {/* <DropdownMenuItem
                // className="py-1 focus:bg-gray-50 focus:text-red-600"
                onClick={(e) => {
                  setIsEditing(true);
                  e.stopPropagation();
                }}
                // className="py-1"
              >
                <Pencil2Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="ml-2 text-sm">Edit</span>
              </DropdownMenuItem> */}
                  <DropdownMenuItem
                    // className="py-1 focus:bg-gray-50 focus:text-red-600"
                    className="focus:bg-red-50 focus:text-red-600"
                    onClick={(e) => {
                      setIsDeleting(true);
                      e.stopPropagation();
                    }}
                  >
                    <TrashIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="ml-2 text-sm">Delete</span>
                  </DropdownMenuItem>
                  {/* <DropdownMenuItem>Delete</DropdownMenuItem> */}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* <Button variant="ghost" className="h-7 w-7 p-0">
              <EllipsisHorizontalIcon className="h-4 w-4" />
            </Button> */}
          </div>
          <Dialog
            open={isDeleting}
            onOpenChange={(open) => {
              setIsDeleting(open);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Confirmation</DialogTitle>
                {/* <DialogDescription>
              Confirm deletion of stage template
            </DialogDescription> */}
              </DialogHeader>
              <div className="space-y-5 pt-3 text-sm text-gray-600">
                <div>Are you sure you want to delete this entry?</div>
              </div>
              <div className="flex items-center justify-end space-x-2">
                <div className="flex justify-end space-x-3">
                  <Button
                    variant={"secondary"}
                    onClick={() => {
                      setIsDeleting(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant={"destructive"}
                    onClick={() => {
                      deleteTimeEntry.mutate({
                        _id: row.original._id,
                      });
                    }}
                    disabled={deleteTimeEntry.isLoading}
                  >
                    {deleteTimeEntry.isLoading ? "Deleting.." : "Delete"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <div className="space-y-6 pt-4">
            <div className="flex space-x-4">
              <div className="space-y-1">
                <InputHeading heading="Date" />
                <TextInput
                  className="h-8 w-40 cursor-not-allowed"
                  value={dayjs(row.original.date).format("ddd, MMM DD")}
                  disabled={true}
                />
              </div>
              <div className="space-y-1">
                <InputHeading heading="Time" />
                <TextInput
                  placeholder="e.g. 2h 30m"
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                  autoFocus={false}
                  className={clsx(
                    !parse(value) &&
                      value !== "" &&
                      "border-red-500 ring-[0.5px] ring-red-500",
                    "h-8 w-40"
                  )}
                  onBlur={(e) => {
                    if (
                      parse(e.target.value) !== time &&
                      !!parse(e.target.value)
                    ) {
                      updateTimeEntry.mutate({
                        entries: [
                          {
                            _id: row.original._id,
                            time: parse(e.target.value) || 0,
                          },
                        ],
                      });
                    }
                    // setTime(parse(e.target.value) || 0);
                  }}
                />
              </div>
              <div className="space-y-1">
                <InputHeading heading="Billable" />
                <Select
                  value={row.original.isBillable ? "true" : "false"}
                  onValueChange={(value) => {
                    updateTimeEntry.mutate({
                      entries: [
                        { _id: row.original._id, isBillable: value === "true" },
                      ],
                    });
                  }}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <InputHeading heading="Description" />
              <textarea
                placeholder="Description..."
                className="w-full resize-none rounded-md border-0 bg-gray-100/70 p-3 text-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-0"
                rows={3}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                onBlur={(e) => {
                  if (e.target.value.trim() !== row.original.description) {
                    updateTimeEntry.mutate({
                      entries: [
                        { _id: row.original._id, description: e.target.value },
                      ],
                    });
                  }
                }}
              />
            </div>
            <div className="space-y-1">
              <InputHeading heading="Project" />
              <div className="w-full text-sm">{row.original.project.name}</div>
            </div>
            {row.original.stage && (
              <div className="space-y-1">
                <InputHeading heading="Stage" />
                <div className="w-full text-sm">{row.original.stage.name}</div>
              </div>
            )}
            {row.original.task && (
              <div className="space-y-1">
                <InputHeading heading="Task" />
                <div className="w-full text-sm">{row.original.task.title}</div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AllTimesheets() {
  const [currentWeek, setCurrentWeek] = useState(getDateOfMonday(new Date()));

  const goToPreviousWeek = () => {
    setCurrentWeek((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };

  const goToNextWeek = () => {
    setCurrentWeek((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };
  const daysOfWeek = useMemo(() => getDaysOfWeek(currentWeek), [currentWeek]);
  const [type, setType] = useState<"total" | "billable" | "non-billable">(
    "total"
  );
  return (
    <div>
      <div className="flex h-14 items-center justify-between border-b border-gray-200/70 px-8">
        <div className="flex space-x-2">
          <div className={clsx("flex rounded-md shadow-sm")}>
            <button
              className={clsx(
                "flex h-8 w-7 items-center justify-center rounded-l-md border border-input text-gray-500 transition-all hover:bg-gray-50 hover:text-gray-700"
              )}
              onClick={goToPreviousWeek}
            >
              <ChevronLeftIcon className="h-3 w-3" />
            </button>
            <div className="flex w-[136px] items-center justify-center border-y border-input px-1 text-sm font-medium text-gray-700 hover:bg-gray-50">
              {`${dayjs(daysOfWeek[0]).format("MMM DD")} - ${dayjs(
                daysOfWeek[6]
              ).format("MMM DD")}`}
            </div>
            <button
              className={clsx(
                "flex h-8 w-7 items-center justify-center rounded-r-md border border-input text-gray-500 transition-all hover:bg-gray-50 hover:text-gray-700"
              )}
              onClick={goToNextWeek}
            >
              <ChevronRightIcon className="h-3 w-3" />
            </button>
          </div>
          <Select
            value={type}
            onValueChange={(value) => {
              setType(value as "total" | "billable" | "non-billable");
            }}
          >
            <SelectTrigger className="w-48 items-center py-0">
              <div className="flex h-full items-center gap-2">
                <div className="inline-flex h-full items-center border-r pr-2">
                  Entries
                </div>
                <div className="flex justify-start">
                  <SelectValue />
                </div>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="total">All</SelectItem>
              <SelectItem value="billable">Billable</SelectItem>
              <SelectItem value="non-billable">Non-Billable</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <TimesheetsTable week={currentWeek} type={type} />
    </div>
  );
}

function TimesheetsTable({
  week,
  type,
}: {
  week: Date;
  type: "total" | "billable" | "non-billable";
}) {
  const { data: timesheets } = useGetTimesheetEntriesForWeek(
    dayjs(week).format("YYYY-MM-DD")
  );

  // const timesheet: TimeEntryData[] = [
  //   {
  //     project: {
  //       _id: "1",
  //       name: "Project 1",
  //     },
  //     stage: {
  //       name: "Installation & Calibration",
  //       _id: "234",
  //     },
  //     task: {
  //       _id: "123",
  //       title: "This is a fairly long task name because on avg they are",
  //     },
  //     _id: "1",
  //     timesheetId: "1",
  //     description: "This is the description for this task",
  //     date: "2024-11-11",
  //     time: 1000 * 60 * 60 * 5,
  //     isBillable: true,
  //     instanceId: "1",
  //     userId: "1",
  //     createdAt: "2024-11-11",
  //     updatedAt: "2024-11-11",
  //     week: "2024-11-11",
  //   },
  // ];

  const columns = useMemo(() => getAllTimesheetsColumns(type), [type]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable<TimesheetSummaryData>({
    data: timesheets ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      // globalFilter: search,
      // columnVisibility: transformColumnsToDisplay(columnsToDisplay),
    },
    // globalFilterFn: fuzzyFilter,
    onSortingChange: setSorting,
    // onGlobalFilterChange: setSearch,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // onColumnVisibilityChange: setColumnsToDisplay,
  });
  return (
    <div className="w-full flex-1 overflow-auto">
      <div className="max-h-full max-w-full overflow-auto">
        <table
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
          className="w-full"
        >
          <thead className="overflow-auto">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={
                      "sticky top-0 z-10 h-11 whitespace-nowrap border-b border-r border-gray-200/70 bg-gray-50 px-6 text-left text-xs font-normal uppercase tracking-wide text-gray-400 first:left-0 first:z-20 first:w-full first:pl-8 last:right-0 last:z-20 last:border-l"
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {!timesheets ? (
              <tr>
                {/* <td className="inset-0 flex items-center justify-center p-4 pl-8"> */}
                <div className="sticky left-0 z-10 p-4 pl-8 text-gray-400">
                  Loading...
                </div>
                {/* </td> */}
              </tr>
            ) : (
              <>
                {timesheets?.length === 0 ? (
                  <div className="flex w-full items-center justify-center">
                    No timesheets
                    {/* <div className="sticky left-0 z-10 whitespace-nowrap p-4 pl-8 text-gray-400">
                    </div> */}
                    {/* <td className="inset-0 flex items-center justify-center p-4">
                </td> */}
                  </div>
                ) : (
                  <>
                    {table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className="group relative rounded-lg"
                        // hover:cursor-pointer hover:bg-gray-50
                        onClick={() => {
                          // navigate(`/projects/${row.original._id}/home`);
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={
                              "fist:px-6 whitespace-nowrap border-r border-b border-gray-100 bg-white px-3 py-3 text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:bg-white first:pr-6 first:font-medium first:text-gray-900 last:sticky last:right-0 last:z-10 last:border-l last:px-6  first:sm:pl-8"
                            }
                            // group-hover:bg-gray-50 first:group-hover:bg-gray-50
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function getAllTimesheetsColumns(
  type: "total" | "billable" | "non-billable"
): ColumnDef<TimesheetSummaryData>[] {
  return [
    {
      id: "member",
      header: () => {
        return <div className="">Member</div>;
      },
      cell: ({ row }) => {
        return (
          <div className="h-full max-w-[400px] truncate">
            {row.original.name}
          </div>
        );
      },
      accessorFn: ({ name }) => name,
    },
    ...DAYS.map((day) => ({
      id: day,
      header: () => {
        return <div className="">{day}</div>;
      },
      cell: ({ row }: { row: Row<TimesheetSummaryData> }) => {
        const totalTime =
          row.original.entries[day as keyof typeof row.original.entries]
            .totalTime;
        const billableTime =
          row.original.entries[day as keyof typeof row.original.entries]
            .totalBilledTime;
        const capacity =
          row.original.entries[day as keyof typeof row.original.entries]
            .capacity;
        const timeToUse =
          type === "billable"
            ? billableTime
            : type === "total"
            ? totalTime
            : totalTime - billableTime;
        const utilization = (totalTime / capacity) * 100;
        const billableUtilization = (billableTime / capacity) * 100;
        return (
          <HoverCard openDelay={400} closeDelay={0}>
            <HoverCardTrigger className="cursor-default">
              <div className="relative flex h-12 w-20 items-center justify-center overflow-hidden rounded-md bg-gray-100/60">
                <div
                  className={clsx(
                    "absolute inset-x-0 bottom-0 bg-purple-200/70"
                  )}
                  style={{
                    height: `${(timeToUse / capacity) * 100}%`,
                  }}
                />
                <div
                  className={clsx(
                    "inset-y-0 z-10",
                    !!timeToUse && "text-gray-700"
                  )}
                >
                  {!!timeToUse ? convertMsToNatural(timeToUse) : "-"}
                </div>
              </div>
            </HoverCardTrigger>
            <HoverCardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-32">Capacity</div>
                  <div className="text-gray-700">
                    {convertMsToNatural(capacity)}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-32">Total Tracked</div>
                  <div className="text-gray-700">
                    {convertMsToNatural(totalTime)}{" "}
                    <span
                      className={clsx(
                        "pl-1 font-medium",
                        utilization >= 0 && utilization < 30 && "text-red-600",
                        utilization >= 30 &&
                          utilization < 70 &&
                          "text-amber-500",
                        utilization >= 70 && "text-green-600"
                      )}
                    >
                      [{utilization.toFixed(0)}%]
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-32">Total Billable</div>
                  <div className="text-gray-700">
                    {convertMsToNatural(billableTime)}{" "}
                    <span
                      className={clsx(
                        "pl-1 font-medium",
                        billableUtilization >= 0 &&
                          billableUtilization < 30 &&
                          "text-red-600",
                        billableUtilization >= 30 &&
                          billableUtilization < 70 &&
                          "text-amber-500",
                        billableUtilization >= 70 && "text-green-600"
                      )}
                    >
                      [{billableUtilization.toFixed(0)}
                      %]
                    </span>
                  </div>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        );
      },
      // accessorFn: ({ entries, day }: { row: Row<UtilizationData> }) => entries[day].totalTime,
    })),
    {
      id: "total",
      header: () => {
        return <div className="">Total</div>;
      },
      cell: ({ row }) => {
        const totalTime = Object.values(row.original.entries).reduce(
          (acc, day) => acc + day.totalTime,
          0
        );

        const totalBilledTime = Object.values(row.original.entries).reduce(
          (acc, day) => acc + day.totalBilledTime,
          0
        );

        const timeToUse =
          type === "billable"
            ? totalBilledTime
            : type === "total"
            ? totalTime
            : totalTime - totalBilledTime;
        return <div className="w-32">{convertMsToNatural(timeToUse)}</div>;
      },
    },
  ];
}
